/**
 * AI Chat Controller - Stimulus controller for AI chat UI
 *
 * Thin orchestrator: lifecycle, input, file upload, stream start.
 * All AI message rendering is handled by Preact components reading from chatStore.
 *
 * Sections:
 * - Lifecycle (connect, disconnect)
 * - View transition (empty state → chat)
 * - Input handlers (enter, resize, file)
 * - Send flow (validation, stream start)
 * - Stream outcome handlers (done, error, guardrail — not UI rendering)
 */

import { Controller } from "@hotwired/stimulus"
import { render, h } from 'preact'
import { rails_direct_uploads_path } from "../routes"
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import { createChatStore } from '../modules/ai/chat-store.js'
import { ChatStreamHandler } from '../modules/ai/chat-stream-handler.js'
import { ChatFormatter } from '../modules/ai/chat-formatter.js'
import { AiChatShell } from '../modules/ai/components/AiChatShell.jsx'
import { ConversationTitle } from '../modules/ai/components/ConversationTitle.jsx'
import {
  getAttachmentType,
  uploadWithDirectUpload,
  createFileEntry,
  getBlobSignedIds,
  getAttachmentsForDisplay,
  hasReadyAttachments,
  hasPendingUploads,
  hasFailedUploads
} from '../modules/ai/attachment-uploader.js'
import { renderFilePreview } from '../modules/ai/file-preview-renderer.js'

const DEV_LOGGING = __DEV_LOGGING__

export default class extends Controller {
  static targets = [
    "output", "input",
    "fileInput", "filePreview", "fileUploadArea",
    "emptyState", "chatContainer",
    "conversationTitle"
  ]
  static values = {
    message:        String,
    userToken:      String,
    streamToken:    String,
    userAvatarUrl:  String,
    conversationId: String,
    agentId:        String,
    readonly:       Boolean,
    hasMessages:    Boolean,
    budgetExceeded: Boolean
  }

  // ── Target helper ──────────────────────────────────────────────────────────

  _visibleTarget(name) {
    const targets = this[`${name}Targets`]
    if (!targets?.length) return this[`${name}Target`]
    const visible = targets.find(el => !el.closest('.d-none'))
    return visible || this[`${name}Target`]
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content
    this.selectedFiles = []
    window.disablePullToRefresh = true

    this.formatter = new ChatFormatter()
    this.store = createChatStore({ i18n: this.i18n })

    // Hydrate pre-rendered ERB messages into the store, then mount Preact
    this.store.hydrateFromDOM(this.outputTarget)
    this._mountPreact()
    this._setupScrollObserver()

    this.streamHandler = new ChatStreamHandler({
      store: this.store,
      onDone: (threadId, msgId) => this._handleDone(threadId, msgId),
      onError: (msg) => this._handleError(msg),
      onGuardrailBlocked: () => this._handleGuardrailBlocked(),
      onQuotaExceeded: (quota) => this._handleQuotaExceeded(quota),
      refreshAuthHeaders: () => this._streamAuthHeaders({ forceRefresh: true }),
    })

    if (this.readonlyValue) return
    if (this.budgetExceededValue) return

    // Fire-and-forget warmup ping so the AgentCore microVM boots while the user types.
    // Fired at most once per mount; skipped for readonly and budget-exceeded states.
    if (!this._warmupFired) {
      this._warmupFired = true
      const streamUrl = __AI_CHAT_STREAM_URL__
      this._streamAuthHeaders().then((authHeaders) => {
        if (!authHeaders.Authorization) return
        return fetch(streamUrl, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            warmup: true,
            conversation_id: this.conversationIdValue,
          }),
        })
      }).catch(() => { /* best-effort */ })
    }

    if (!this.messageValue) return

    if (this.hasMessagesValue) {
      if (DEV_LOGGING) console.log('[AI Chat] Has messages, starting stream after redirect')
      this._startStreamAfterRedirect(this.messageValue)
      return
    }

    if (DEV_LOGGING) console.log('[AI Chat] No messages, adding user message and starting stream')
    this.store.addUserMessage({
      content: this.messageValue,
      avatarUrl: this.userAvatarUrlValue || null
    })
    this.openStream(this.messageValue)
  }

  disconnect() {
    window.disablePullToRefresh = false
    this.streamHandler?.abort()
    this._teardownScrollObserver()
    // Unmount Preact cleanly
    render(null, this.outputTarget)
    if (this.hasConversationTitleTarget) {
      render(null, this.conversationTitleTarget)
    }
  }

  // ── Scroll observer ────────────────────────────────────────────────────────

  _setupScrollObserver() {
    const scrollEl = this.outputTarget
    // Scroll to bottom unconditionally on initial mount (no user scroll yet)
    scrollEl.scrollTop = scrollEl.scrollHeight

    this._userScrolledUp = false
    this._scrollListener = () => {
      const atBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 80
      this._userScrolledUp = !atBottom
    }
    scrollEl.addEventListener('scroll', this._scrollListener, { passive: true })

    this._mutationObserver = new MutationObserver(() => {
      if (!this._userScrolledUp) {
        requestAnimationFrame(() => { scrollEl.scrollTop = scrollEl.scrollHeight })
      }
    })
    this._mutationObserver.observe(scrollEl, { childList: true, subtree: true })
  }

  _teardownScrollObserver() {
    this._mutationObserver?.disconnect()
    this._mutationObserver = null
    if (this._scrollListener) {
      this.outputTarget.removeEventListener('scroll', this._scrollListener)
      this._scrollListener = null
    }
  }

  _mountPreact() {
    render(
      h(AiChatShell, {
        store: this.store,
        formatter: this.formatter,
        onRegisterQuotaHandler: (handler) => { this._quotaCapture = handler }
      }),
      this.outputTarget
    )
    if (this.hasConversationTitleTarget) {
      render(
        h(ConversationTitle, { store: this.store }),
        this.conversationTitleTarget
      )
    }
  }

  // ── View transition ────────────────────────────────────────────────────────

  transitionToChatView() {
    if (this.hasEmptyStateTarget)   this.emptyStateTarget.classList.add('d-none')
    if (this.hasChatContainerTarget) {
      this.chatContainerTarget.classList.remove('d-none')
      this.chatContainerTarget.classList.add('chat-has-messages')
    }
    this.element.classList.remove('ai-chat-empty-page')
    this.element.classList.add('ai-chat-messages-page')
    this.element.closest('.ai-chat-page')?.classList.remove('ai-chat-empty')
    // Re-mount Preact into the now-visible output container
    this._mountPreact()
    // Force scroll to bottom after the container becomes visible;
    // rAF lets the browser finish layout before reading scrollHeight.
    requestAnimationFrame(() => {
      const scrollEl = this.outputTarget
      scrollEl.scrollTop = scrollEl.scrollHeight
      this._userScrolledUp = false
    })
  }

  _startStreamAfterRedirect(message) {
    const startStream = () => {
      if (!this.element?.isConnected) return
      if (DEV_LOGGING) console.log('[AI Chat] Redirect complete, starting stream')
      this.openStream(message)
    }
    if (document.documentElement.hasAttribute('data-turbo-preview')) {
      const handler = () => {
        document.removeEventListener('turbo:load', handler)
        startStream()
      }
      document.addEventListener('turbo:load', handler)
    } else {
      requestAnimationFrame(() => requestAnimationFrame(startStream))
    }
  }

  // ── Input handlers ─────────────────────────────────────────────────────────

  handleEnterKey(event) {
    if (event.shiftKey) return
    event.preventDefault()
    this.send(event)
  }

  autoResize(event) {
    const textarea = event.target
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'
  }

  triggerFileInput(event) {
    event.preventDefault()
    this._visibleTarget('fileInput').click()
  }

  async handleFileSelect(event) {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    this.selectedFiles = this.selectedFiles || []
    const directUploadUrl = typeof rails_direct_uploads_path === 'function'
      ? rails_direct_uploads_path()
      : '/rails/active_storage/direct_uploads'

    for (const file of files) {
      const type       = getAttachmentType(file)
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      const entry      = createFileEntry(file, type, previewUrl)
      this.selectedFiles.push(entry)
      this.updateFilePreview()

      try {
        const blob   = await uploadWithDirectUpload(file, directUploadUrl)
        entry.signedId = blob.signed_id
        entry.status   = 'done'
      } catch (err) {
        console.error('Direct upload failed:', file.name, err)
        entry.status = 'error'
        entry.error  = err.message
      }
      this.updateFilePreview()
    }
    event.target.value = ''
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this.updateFilePreview()
  }

  updateFilePreview() {
    const filePreview   = this._visibleTarget('filePreview')
    const fileUploadArea = this._visibleTarget('fileUploadArea')
    renderFilePreview(filePreview, fileUploadArea, this.selectedFiles)
  }

  // ── Send flow ──────────────────────────────────────────────────────────────

  async send(event) {
    event?.preventDefault?.()
    if (this.readonlyValue || this.budgetExceededValue) return

    const input         = this._visibleTarget('input')
    const message       = input.value?.trim?.() || ''
    const hasAttachments = hasReadyAttachments(this.selectedFiles)

    if (!message && !hasAttachments) return
    if (hasPendingUploads(this.selectedFiles)) return
    if (hasFailedUploads(this.selectedFiles))  return

    const blobSignedIds       = getBlobSignedIds(this.selectedFiles)
    const attachmentsForDisplay = getAttachmentsForDisplay(this.selectedFiles)

    if (!this.hasMessagesValue) {
      this.transitionToChatView()
    }

    this.store.addUserMessage({
      content: message,
      attachments: attachmentsForDisplay,
      avatarUrl: this.userAvatarUrlValue || null
    })
    this._clearInputState(input)
    this.openStream(message, [], blobSignedIds)
  }

  _clearInputState(input) {
    input.value = ''
    if (input.tagName === 'TEXTAREA') input.style.height = 'auto'
    this.selectedFiles = []
    this.updateFilePreview()
  }

  async openStream(message, attachments = [], blobSignedIds = []) {
    try {
      if (DEV_LOGGING) console.log('[AI Chat] Opening stream', { blobSignedIds: blobSignedIds?.length })
      this.store.setStatus('start', this.i18n.t('ai.codes.start'))

      const streamUrl     = __AI_CHAT_STREAM_URL__
      const streamHeaders = {
        'X-Conversation-Id': this.conversationIdValue,
        ...(await this._streamAuthHeaders()),
      }

      await this.streamHandler.startStream(
        streamUrl, message, attachments, blobSignedIds,
        streamHeaders
      )
    } catch (error) {
      console.error('[AI Chat] Stream error:', error)
      this._handleError(error.message || 'An error occurred during processing.')
    }
  }

  // ── aichat-service caller token ───────────────────────────────────────────
  // ai.tshmanager.* /stream authenticates the caller with a short-lived token
  // issued by Rails (Ai::StreamToken). The page embeds one; long-open pages
  // refresh it from /ai/stream_token shortly before it expires or after a 401.

  _streamTokenExp(token) {
    try {
      const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
      return Number(JSON.parse(atob(part)).exp) || 0
    } catch (_e) {
      return 0
    }
  }

  async _fetchStreamToken() {
    try {
      const res = await fetch('/ai/stream_token', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (!res.ok) return null
      const data = await res.json()
      if (!data?.token) return null
      this.streamTokenValue = data.token
      return data.token
    } catch (_e) {
      return null
    }
  }

  async _streamAuthHeaders({ forceRefresh = false } = {}) {
    let token = this.hasStreamTokenValue ? this.streamTokenValue : ''
    const now = Math.floor(Date.now() / 1000)
    if (forceRefresh || !token || this._streamTokenExp(token) - now < 60) {
      token = await this._fetchStreamToken()
    }
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  // ── Stream outcome handlers ────────────────────────────────────────────────

  async _handleDone(conversationId, msgId) {
    if (DEV_LOGGING) console.log('[AI Chat] Done:', conversationId, 'msgId:', msgId)

    // Update conversation URL if new
    if (conversationId) {
      this.conversationIdValue = conversationId.toString()
      const newUrl = `/ai/conversations/${conversationId}`
      if (window.location.pathname !== newUrl) {
        window.history.replaceState({}, '', newUrl)
      }
    }

    // Budget exceeded flag
    if (this.store._budgetExceeded) {
      this.budgetExceededValue = true
    }
  }

  _handleError(message) {
    if (DEV_LOGGING) console.error('[AI Chat] Error:', message)
    const text = message === 'budget_exceeded'
      ? `${this.i18n.t('ai.budget_exceeded.title')}: ${this.i18n.t('ai.budget_exceeded.badge')}`
      : message
    this.store.setStatus('error', text || this.i18n.t('ai.error_occurred'))
  }

  _handleGuardrailBlocked() {
    if (DEV_LOGGING) console.warn('[AI Chat] Guardrail blocked')
    const msg = this.i18n.t('ai.guardrail_blocked')
    // Hostagent suppresses the Bedrock redact text from TEXT_MESSAGE_CONTENT;
    // put the localized message into the AI bubble so the turn is not blank.
    if (!this.store.getFinalBuffer()) {
      this.store.appendTextDelta(msg)
    }
    this.store.setStatus('error', msg)
  }

  _handleQuotaExceeded(quota) {
    if (DEV_LOGGING) console.warn('[AI Chat] Quota exceeded', quota)
    this.budgetExceededValue = true
    this._quotaCapture?.(quota)
    this.store.runErrorFinish?.()
  }
}
